import notifications from '@/api/modules/notifications'
import updateRecord from '@/utils/update-record'
import { AppHolder } from '@/utils/app-holder'
import humps from 'humps'
import findIndex from 'lodash/findIndex'
import { notificationKinds } from '@/api/api-values'

const getDefaultState = () => {
  return {
    newList: [],
    list: [],
    total: 0,
    nbPages: 1,
    lastPage: 1,
    nbUnread: 0
  }
}

const state = getDefaultState()

const actions = {
  fetch (context, { page, cached } = { page: 1, cached: true }) {
    if (!page) {
      page = 1
    }
    if (cached && page === context.state.lastPage && context.state.list.length) {
      return Promise.resolve()
    }

    return notifications.fetch(page)
      .then(data => {
        context.commit('setLastPage', page)
        context.commit('fetch', data)
      })
  },

  add (context, data) {
    const notif = {
      ...data,
      createdAt: Date.now(),
      event: humps.camelize(data.event)
    }

    // Reject unknown kinds
    if (!notificationKinds.includes(notif.kind)) {
      return
    }

    // Emit to listeners
    AppHolder.bus.$emit(notif.kind, notif)

    // Commit to store
    if (notif.kind !== 'system' &&
      notif.payload &&
      notif.payload.notificationId
    ) {
      context.commit('add', notif)
    }
  },

  read (context, ids) {
    if (!Array.isArray(ids)) {
      ids = [ids]
    }
    return Promise.all(ids.map(id => notifications.read(id)))
      .then(() => ids.forEach(id => context.commit('setRead', id)))
  },

  readAll (context) {
    return notifications.readAll()
      .then(() => context.state.list.forEach(notification => context.commit('setRead', notification.id)))
      .then(() => context.commit('setNbUnread', 0))
  },

  resetState (context) {
    context.commit('resetState')
  }
}

const mutations = {
  fetch (state, { data, meta }) {
    const { list } = state
    data && data.forEach(notification => updateRecord(list, {
      ...notification,
      event: humps.camelize(notification.event),
      createdAt: notification.createdAt * 1000
    }))
    state.list = [...state.list]
    state.nbPages = meta.totalPages
    state.nbUnread = meta.unreadNb
  },

  add (state, data) {
    const index = findIndex(state.list, { id: data.payload.notificationId })
    if (index === -1) {
      state.list = [
        ...state.list,
        {
          ...data,
          id: data.payload.notificationId
        }
      ]
    } else {
      state.list = [
        ...state.list.slice(0, index),
        {
          ...data,
          id: data.payload.notificationId
        },
        ...state.list.slice(index + 1)
      ]
    }

    state.newList = [
      ...state.newList,
      data
    ]
  },

  setLastPage (state, page) {
    state.lastPage = page
  },

  setRead (state, id) {
    const listIndex = findIndex(state.list, { id })
    const newListIndex = findIndex(state.newList, { payload: { notificationId: id } })
    if (listIndex >= 0 && !state.list[listIndex].read) {
      state.list = [
        ...state.list.slice(0, listIndex),
        { ...state.list[listIndex], read: true },
        ...state.list.slice(listIndex + 1)
      ]
      if (state.nbUnread > 0) {
        state.nbUnread--
      }
    }
    if (newListIndex >= 0) {
      state.newList = [
        ...state.newList.slice(0, newListIndex),
        { ...state.newList[newListIndex], read: true },
        ...state.newList.slice(newListIndex + 1)
      ]
    }
  },

  setNbUnread (state, nbUnread) {
    state.nbUnread = nbUnread
  },

  resetState (state) {
    Object.assign(state, getDefaultState())
  }
}

const getters = {
  unread ({ newList }) {
    return newList.filter(notification => !notification.read).length
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  mutations,
  getters
})
